import Fuse from "fuse.js";
import { Fragment, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ScrollView, type TextInput } from "react-native";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { Divider } from "../Divider";
import { Icon } from "../Icon";
import { Pressable } from "./Pressable";
import { BodyText } from "./Text/BodyText";
import { TextInputV2 } from "./TextInputV2";
import { View } from "./View";
import type { PressableState } from "./Pressable";
import type { FuseResult } from "fuse.js";

type DropdownItem = {
  id: string;
  title: string;
};

interface DropdownProps {
  label: string;
  items: DropdownItem[];
  selectedItemId: string | undefined | null;
  onSelectItem: (item?: string) => void;
  initialSelectedItemId?: string | null;
  disabled?: boolean;
  isError?: boolean;
  helperText?: string;
  floating?: boolean;
}

/**
 * Dropdown component for selecting an item from a list.
 * Supports search functionality and optional floating positioning.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the dropdown input
 * @param {DropdownItem[]} props.items - Array of dropdown items
 * @param {string | undefined | null} props.selectedItemId - ID of the selected item
 * @param {(item?: string) => void} props.onSelectItem - Callback function when an item is selected
 * @param {string | null} [props.initialSelectedItemId] - Initial selected item ID
 * @param {boolean} [props.disabled=false] - Whether the dropdown is disabled
 * @param {boolean} [props.isError=false] - Whether the dropdown is in an error state
 * @param {string} [props.helperText] - Helper text displayed below the input
 * @param {boolean} [props.floating=true] - Whether the dropdown is absolutely positioned (floating)
 *
 * @returns {JSX.Element} The Dropdown component
 */
export function Dropdown({
  label,
  items,
  selectedItemId,
  onSelectItem,
  initialSelectedItemId,
  disabled = false,
  isError = false,
  helperText,
  floating = true,
}: DropdownProps) {
  const initialSelectedItem = items.find(
    (item) => initialSelectedItemId && item.id === initialSelectedItemId,
  );

  const [searchTerm, setSearchTerm] = useState(
    initialSelectedItem?.title || "",
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const containerRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const dropdownListRef = useRef<ScrollView>(null);
  const dropdownIcon = useRef<Pressable>(null);

  const dropdownSearch = new Fuse<DropdownItem>(items, {
    threshold: 0.3,
    keys: ["title"],
  });

  useEffect(() => {
    const eventListener = (e: MouseEvent) => {
      if (
        e.target !== inputRef.current &&
        e.target !== dropdownListRef.current &&
        e.target !== containerRef.current &&
        e.target !== dropdownIcon.current
      ) {
        setIsDropdownVisible(false);
        if (initialSelectedItemId && !selectedItemId) {
          setSearchTerm(initialSelectedItem?.title || "");
          onSelectItem(initialSelectedItemId);
        }
        // else {
        //   setSearchTerm("");
        //   onSelectItem(undefined);
        // }
        e.stopPropagation();
      }
    };
    window.addEventListener("click", eventListener);

    return () => window.removeEventListener("click", eventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedItemId, selectedItemId]);

  const filteredItems = dropdownSearch.search(searchTerm);
  const dropdownItems = searchTerm ? filteredItems : items;

  return (
    <>
      <View ref={containerRef} style={{ zIndex: 10 }}>
        <TextInputV2
          ref={inputRef}
          value={searchTerm}
          onChangeText={(value) => {
            setSearchTerm(value);
          }}
          label={label}
          labelColor={
            !isDropdownVisible && isError
              ? styleConstantsV2.colorError
              : undefined
          }
          onFocus={() => {
            setIsDropdownVisible(true);
            setSearchTerm("");
            onSelectItem(undefined);
          }}
          textBelowComponent={
            helperText && (
              <BodyText
                sizeSmall
                colorError={isError}
                colorGrey={!isError}
                weightRegular
              >
                {helperText}
              </BodyText>
            )
          }
          style={[
            isError
              ? {
                  borderColor: styleConstantsV2.colorError,
                  backgroundColor: styleConstantsV2.colorErrorBg,
                }
              : undefined,
            isDropdownVisible
              ? {
                  borderColor: styleConstantsV2.colorPrimaryMid,
                  backgroundColor: styleConstantsV2.colorWhite,
                  borderBottomStartRadius: 0,
                  borderBottomEndRadius: 0,
                }
              : undefined,
          ]}
          rightComponent={
            <Pressable
              ref={dropdownIcon}
              disabled={disabled}
              onPress={() => {
                if (isDropdownVisible) {
                  setIsDropdownVisible(false);
                  if (initialSelectedItemId && !selectedItemId) {
                    setSearchTerm(initialSelectedItem?.title || "");
                    onSelectItem(initialSelectedItemId);
                  }
                }
                if (!isDropdownVisible) {
                  inputRef.current?.focus();
                  setSearchTerm("");
                  onSelectItem(undefined);
                }
              }}
            >
              <Icon
                name={isDropdownVisible ? "chevron-up" : "chevron-down"}
                size={24}
                color={
                  disabled
                    ? styleConstantsV2.colorGrey20
                    : styleConstantsV2.colorBlack
                }
              />
            </Pressable>
          }
        />
        {isDropdownVisible && (
          <ScrollView
            ref={dropdownListRef}
            style={{
              borderLeftWidth: 2,
              borderRightWidth: 2,
              borderBottomWidth: 2,
              borderColor: styleConstantsV2.colorPrimaryMid,
              position: floating ? "absolute" : "static",
              top: 69,
              width: "100%",
              maxHeight: 250,
              borderBottomLeftRadius: styleConstantsV2.borderRadius8,
              borderBottomRightRadius: styleConstantsV2.borderRadius8,
              backgroundColor: styleConstantsV2.colorWhite,
            }}
          >
            {dropdownItems.length === 0 ? (
              <View
                colorWhite
                justifyContentCenter
                alignItemsCenter
                style={{
                  paddingVertical: styleConstantsV2.spacing12,
                  paddingHorizontal: styleConstantsV2.spacing24,
                  borderBottomRightRadius: styleConstantsV2.borderRadius8,
                  borderBottomLeftRadius: styleConstantsV2.borderRadius8,
                }}
              >
                <BodyText colorGrey>
                  <FormattedMessage
                    id="OiFGuz"
                    defaultMessage="No search results"
                    description="Text in dropdown when no search results are found"
                  />
                </BodyText>
              </View>
            ) : (
              dropdownItems.map((item, index) => {
                const isLastItem = index === dropdownItems.length - 1;
                const dropdownItem = searchTerm
                  ? (item as FuseResult<DropdownItem>).item
                  : (item as DropdownItem);

                return (
                  <Fragment key={dropdownItem.id}>
                    <Pressable
                      onPress={() => {
                        setSearchTerm(dropdownItem.title);
                        onSelectItem(dropdownItem.id);
                        setIsDropdownVisible(false);
                      }}
                      style={({ hovered }: Omit<PressableState, "focused">) => {
                        return {
                          paddingVertical: styleConstantsV2.spacing12,
                          paddingHorizontal: styleConstantsV2.spacing24,
                          borderBottomRightRadius: isLastItem
                            ? styleConstantsV2.borderRadius8
                            : 0,
                          borderBottomLeftRadius: isLastItem
                            ? styleConstantsV2.borderRadius8
                            : 0,
                          backgroundColor: hovered
                            ? styleConstantsV2.colorGrey20
                            : styleConstantsV2.colorWhite,
                        };
                      }}
                    >
                      <BodyText sizeLarge>{dropdownItem.title}</BodyText>
                    </Pressable>
                    {!isLastItem && <Divider />}
                  </Fragment>
                );
              })
            )}
          </ScrollView>
        )}
      </View>
    </>
  );
}
